// ── the one record ───────────────────────────────────────────────────────────
//
// Single source of truth for everything innernet learns about a person: a fact
// or a pattern has ONE home, the private view, where its words, its class,
// where it came from and when it was found are kept together. Anything that
// shows it elsewhere (the inbox, a share card, the day digest, a mirror line)
// carries only a pointer and links back here, so there is never a second copy
// to drift out of date or survive a forget.
//
// The pointer is `RecordRef`. The private view renders each record under the
// id `recordAnchor(ref)`; `openRecord(ref)` asks the shell to bring the private
// view up and light that one record. Pure and client-safe.

export type RecordKind = 'fact' | 'pattern';

export interface RecordRef {
  kind: RecordKind;
  id: string;
}

/** the DOM id a record wears on the private view (`#fact-<id>`, `#pattern-<id>`). */
export function recordAnchor(ref: RecordRef): string {
  return `${ref.kind}-${ref.id}`;
}

/** parse a hash or anchor back into a pointer ("#fact-<uuid>" → { kind, id }). */
export function parseRecordAnchor(raw: string | null | undefined): RecordRef | null {
  if (!raw) return null;
  // a uuid in production; the /mirror demo's ids are short slugs (sf_4a16…)
  const m = /^#?(fact|pattern)-([0-9a-z_-]{6,64})$/i.exec(raw.trim());
  return m ? { kind: m[1].toLowerCase() as RecordKind, id: m[2].toLowerCase() } : null;
}

/** the address of a record's home: the you panel's private view, at the record. */
export function recordHref(ref: RecordRef): string {
  return `/dashboard?panel=you&view=private#${recordAnchor(ref)}`;
}

/** the event the shell and the private view listen for. */
export const OPEN_RECORD_EVENT = 'innernet:open-record';

/** bring the private view up and light this record (the shell routes it). */
export function openRecord(ref: RecordRef): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<RecordRef>(OPEN_RECORD_EVENT, { detail: ref }));
}

/** what a forget took: every id of that kind that is gone (a fact's whole version chain). */
export interface RecordForgotten {
  kind: RecordKind;
  ids: string[];
}

/** the event a landed forget sends, so every pointer on the page (the inbox) lets go too. */
export const RECORD_FORGOTTEN_EVENT = 'innernet:record-forgotten';

/** tell the page a record is gone (only after the server said so). */
export function recordForgotten(kind: RecordKind, ids: readonly string[]): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  window.dispatchEvent(new CustomEvent<RecordForgotten>(RECORD_FORGOTTEN_EVENT, { detail: { kind, ids: [...ids] } }));
}
